import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    IconButton,
    CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import apilink from "../globals/Baseurls";
import { useNavigate } from "react-router-dom";

const redTextFieldStyle = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
        "& fieldset": { borderColor: "#676666ff" },
        "&:hover fieldset": { borderColor: "#010832" },
        "&.Mui-focused fieldset": { borderColor: "#010832" },
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#010832",
    },
};
const redTextFieldLasts = {
    ml: 1,
    flex: 1,
    "& .MuiOutlinedInput-root": {
        "& fieldset": { borderColor: "#676666ff" },
        "&:hover fieldset": { borderColor: "#010832" },
        "&.Mui-focused fieldset": { borderColor: "#010832" },
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#010832",
    },
};

export default function UpdateProfile() {
    const [profile, setProfile] = useState({
        display_name: "",
        gender: "male",
        relation_type: "",
        birthdate: "",
        bio: "",
        job_title: "",
        education: "",
        location_lat: "",
        location_lng: "",
        image: null, // current profile pic
    });

    const [imageFile, setImageFile] = useState(null);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    // Fetch current profile on mount
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const userData = JSON.parse(localStorage.getItem("userSessionData"));
                const token = userData.token;

                const response = await axios.get(`${apilink.link}/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                console.log(response.data);

                setProfile((prev) => ({
                    ...prev,
                    display_name: response.data.profile.display_name,
                    gender: response.data.profile.gender,
                    relation_type: response.data.profile.relation_type,
                    birthdate: response.data.profile.birthdate,
                    bio: response.data.profile.bio,
                    job_title: response.data.profile.job_title,
                    education: response.data.profile.education,
                    location_lat: response.data.profile.location_lat,
                    location_lng: response.data.profile.location_lng,
                    image:
                        response.data.photos?.find((p) => p.is_primary)?.path || null,
                }));
            } catch (err) {
                console.error(err.response?.data || err.message);
            }
        };

        fetchProfile();
    }, []);

    // Handle image selection
    const handleImageChange = (e) => {
        if (e.target.files.length > 0) {
            setImageFile(e.target.files[0]);
            setProfile((prev) => ({
                ...prev,
                image: URL.createObjectURL(e.target.files[0]),
            }));
        }
    };

    // Use current location
    const useCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert("Geolocation not supported by your browser");
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setProfile((prev) => ({
                    ...prev,
                    location_lat: pos.coords.latitude,
                    location_lng: pos.coords.longitude,
                }));
            },
            (err) => {
                console.error(err);
                alert("Unable to get current location");
            }
        );
    };

    // Submit updated profile
    const handleSubmit = async () => {
        setLoading(true);
        try {
            const userData = JSON.parse(localStorage.getItem("userSessionData"));
            const token = userData.token;

            const formData = new FormData();

            // Append profile fields (exclude image preview)
            const {
                image,
                ...profileFields
            } = profile;

            Object.keys(profileFields).forEach((key) => {
                if (profileFields[key] !== null && profileFields[key] !== "") {
                    formData.append(key, profileFields[key]);
                }
            });

            // Append single photo
            if (imageFile) {
                formData.append("photo", imageFile);
            }

            const response = await axios.post(
                `${apilink.link}/profile-update`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data.message) {
                alert("Profile updated successfully!");
                navigate("/profile");
            }
        } catch (err) {
            console.error(err.response?.data || err.message);
            alert("Failed to update profile");
        } finally {
            setLoading(false);
        }
    };


    return (
        <Box p={2} maxWidth={500} mx="auto">
            {/* Top row: Back icon + Title */}
            <Box display="flex" alignItems="center" mb={2}>
                <IconButton
                    onClick={() => navigate("/profile")}
                    sx={{
                        bgcolor: "#f0f0f0",       // faint gray
                        width: 40,                 // fixed width
                        height: 40,                // fixed height
                        borderRadius: "50%",       // perfect circle
                        display: "flex",
                        alignItems: "center",      // vertical center
                        justifyContent: "center",  // horizontal center
                        "&:hover": {
                            bgcolor: "#e0e0e0",
                        },
                    }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" ml={1}>
                    Edit Profile
                </Typography>
            </Box>
            {/* Profile picture */}
            <Box
                position="relative"
                width={150}
                height={150}
                mx="auto"
                mb={3}
                borderRadius={3}
                overflow="hidden"
                boxShadow={3}
            >
                {profile.image ? (
                    <img
                        src={
                            profile.image?.startsWith("blob:") ||
                                profile.image?.startsWith("http")
                                ? profile.image
                                : `${apilink.base_link}/storage/${profile.image}`
                        }
                        alt="Profile"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                ) : (
                    <Box
                        bgcolor="#ddd"
                        width="100%"
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography>No Photo</Typography>
                    </Box>
                )}

                {/* Change photo button */}
                <IconButton
                    component="label"
                    sx={{
                        position: "absolute",
                        bottom: 4,
                        right: 4,
                        bgcolor: "rgba(255,255,255,0.85)",
                        "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    }}
                >
                    <EditIcon />
                    <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                </IconButton>
            </Box>

            {/* Text fields */}
            <TextField
                label="Display Name"
                fullWidth
                sx={redTextFieldStyle}
                value={profile.display_name}
                onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
            />
            <TextField
                label="Gender"
                fullWidth
                sx={redTextFieldStyle}
                select
                SelectProps={{ native: true }}
                value={profile.gender}
                onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
            >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non_binary">Non-binary</option>
                <option value="other">Other</option>
            </TextField>
            <TextField
                label="Relation Type"
                fullWidth
                sx={redTextFieldStyle}
                value={profile.relation_type}
                onChange={(e) => setProfile({ ...profile, relation_type: e.target.value })}
            />
            <TextField
                label="Birthdate"
                type="date"
                fullWidth
                sx={redTextFieldStyle}
                InputLabelProps={{ shrink: true }}
                value={profile.birthdate}
                onChange={(e) => setProfile({ ...profile, birthdate: e.target.value })}
            />
            <TextField
                label="Bio"
                multiline
                rows={3}
                fullWidth
                sx={redTextFieldStyle}
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            />
            <TextField
                label="Job Title"
                fullWidth
                sx={redTextFieldStyle}
                value={profile.job_title}
                onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
            />
            <TextField
                label="Education"
                fullWidth
                sx={redTextFieldStyle}
                value={profile.education}
                onChange={(e) => setProfile({ ...profile, education: e.target.value })}
            />

            {/* Location */}
            <Box display="flex" alignItems="center" mb={2}>
                <TextField
                    label="Latitude"
                    type="number"
                    sx={redTextFieldLasts}
                    value={profile.location_lat}
                    onChange={(e) => setProfile({ ...profile, location_lat: e.target.value })}
                />
                <TextField
                    label="Longitude"
                    type="number"
                    sx={redTextFieldLasts}
                    value={profile.location_lng}
                    onChange={(e) => setProfile({ ...profile, location_lng: e.target.value })}
                />
                <Button onClick={useCurrentLocation}>Use Current Location</Button>
            </Box>

            {/* Submit */}
            <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                sx={{
                    bgcolor: "#010832",
                    "&:hover": {
                        bgcolor: "#b81820",
                    },
                    mb: 4
                }}
                disabled={loading}
            >
                {loading ? <CircularProgress size={24} /> : "Update Profile"}
            </Button>
        </Box>
    );
}
